import React, { useState } from 'react';
import { ArrowLeft, Plus, Search, Filter, Calendar, TrendingUp, DollarSign, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface Investimento {
  id: string;
  tipo: 'aplicacao' | 'cdb' | 'poupanca' | 'tesouro' | 'outros';
  descricao: string;
  valor: number;
  dataInvestimento: string;
  dataVencimento?: string;
  rentabilidade?: number;
  status: 'ativo' | 'vencido' | 'resgatado';
  responsavel: string;
  observacoes?: string;
}



const InvestimentosPage: React.FC = () => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({
    tipo: '',
    descricao: '',
    status: '',
    dataInicio: '',
    dataFim: ''
  });

  // Dados mockados
  const [investimentos, setInvestimentos] = useState<Investimento[]>([
    {
      id: '1',
      tipo: 'poupanca',
      descricao: 'Reserva de Emergência',
      valor: 15000.00,
      dataInvestimento: '2024-01-15',
      rentabilidade: 6.17,
      status: 'ativo',
      responsavel: 'Tesoureiro João',
      observacoes: 'Conta poupança para emergências'
    },
    {
      id: '2',
      tipo: 'cdb',
      descricao: 'CDB Banco do Brasil',
      valor: 25000.00,
      dataInvestimento: '2024-02-01',
      dataVencimento: '2025-02-01',
      rentabilidade: 12.5,
      status: 'ativo',
      responsavel: 'Tesoureiro João',
      observacoes: 'CDB 120% CDI'
    },
    {
      id: '3',
      tipo: 'tesouro',
      descricao: 'Tesouro Selic 2029',
      valor: 10000.00,
      dataInvestimento: '2024-03-10',
      dataVencimento: '2029-03-01',
      rentabilidade: 10.75,
      status: 'ativo',
      responsavel: 'Pastor Silva',
      observacoes: 'Investimento de longo prazo'
    }
  ]);



  const statusOrder = ['ativo', 'vencido', 'resgatado'];

  const handleStatusClick = (id: string) => {
    setInvestimentos(prev => prev.map(investimento => {
      if (investimento.id === id) {
        const currentIndex = statusOrder.indexOf(investimento.status);
        const nextIndex = (currentIndex + 1) % statusOrder.length;
        return { ...investimento, status: statusOrder[nextIndex] as 'ativo' | 'vencido' | 'resgatado' };
      }
      return investimento;
    }));
  };

  const totalInvestido = investimentos.reduce((acc, inv) => acc + inv.valor, 0);
  const investimentosAtivos = investimentos.filter(inv => inv.status === 'ativo').length;
  const rentabilidadeMedia = investimentos.reduce((acc, inv) => acc + (inv.rentabilidade || 0), 0) / investimentos.length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-green-500';
      case 'vencido': return 'bg-yellow-500';
      case 'resgatado': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ativo': return 'Ativo';
      case 'vencido': return 'Vencido';
      case 'resgatado': return 'Resgatado';
      default: return status;
    }
  };

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case 'aplicacao': return 'Aplicação Financeira';
      case 'cdb': return 'CDB';
      case 'poupanca': return 'Poupança';
      case 'tesouro': return 'Tesouro Direto';
      case 'outros': return 'Outros';
      default: return tipo;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Investimentos</h1>
              <p className="text-muted-foreground">Gestão de investimentos e aplicações financeiras</p>
            </div>
          </div>
          <Button onClick={() => navigate('/financas/administracao/investimentos/cadastrar')} className="bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Investimento
          </Button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Investido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-500">
                  R$ {totalInvestido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Investimentos Ativos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Target className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-2xl font-bold text-blue-500">{investimentosAtivos}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Rentabilidade Média</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-2xl font-bold text-yellow-500">
                  {rentabilidadeMedia.toFixed(2)}%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total de Investimentos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Target className="h-4 w-4 text-purple-500 mr-2" />
                <span className="text-2xl font-bold text-purple-500">{investimentos.length}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e Tabela de Investimentos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros e Registros de Investimentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Filtros */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="filtro-tipo">Tipo</Label>
                  <Select value={filtros.tipo} onValueChange={(value) => setFiltros({...filtros, tipo: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      <SelectItem value="aplicacao">Aplicação Financeira</SelectItem>
                      <SelectItem value="cdb">CDB</SelectItem>
                      <SelectItem value="poupanca">Poupança</SelectItem>
                      <SelectItem value="tesouro">Tesouro Direto</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filtro-descricao">Descrição</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="filtro-descricao"
                      placeholder="Buscar descrição..."
                      value={filtros.descricao}
                      onChange={(e) => setFiltros({...filtros, descricao: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="filtro-status">Status</Label>
                  <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="vencido">Vencido</SelectItem>
                      <SelectItem value="resgatado">Resgatado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filtro-data-inicio">Data Inicial</Label>
                  <Input
                    id="filtro-data-inicio"
                    type="date"
                    value={filtros.dataInicio}
                    onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="filtro-data-fim">Data Final</Label>
                  <Input
                    id="filtro-data-fim"
                    type="date"
                    value={filtros.dataFim}
                    onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                  />
                </div>
              </div>

              {/* Tabela */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">TIPO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">DESCRIÇÃO</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">VALOR</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">DATA</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">RENTABILIDADE</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">STATUS</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">RESPONSÁVEL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investimentos.map((investimento) => (
                      <tr key={investimento.id} className="border-b">
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-blue-400 border-blue-400">
                            {getTipoText(investimento.tipo)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">{investimento.descricao}</td>
                        <td className="py-3 px-4 font-medium">
                          R$ {investimento.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                            <span>
                              {new Date(investimento.dataInvestimento).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {investimento.rentabilidade ? (
                            <div className="flex items-center">
                              <TrendingUp className="h-4 w-4 text-green-400 mr-2" />
                              <span>
                                {investimento.rentabilidade}% a.a.
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            className={`${getStatusColor(investimento.status)} text-white cursor-pointer relative group`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleStatusClick(investimento.id);
                            }}
                            title="Clique para mudar status"
                          >
                            {getStatusText(investimento.status)}
                            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                              Clique para mudar status
                            </span>
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">{investimento.responsavel}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>


      </div>
    </div>
  );
};

export default InvestimentosPage;
export { InvestimentosPage };